import { useEffect, useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Patient } from "@/lib/types/models.ts";

interface PatientAutocompleteProps {
  value?: Patient | null;
  onChange?: (patient: Patient | null | undefined) => void;
  placeholder?: string;
}

export default function PatientAutocomplete({
                                              value: valueProp = null,
                                              onChange,
                                              placeholder = "Pretraži pacijente...",
                                            }: PatientAutocompleteProps) {
  const [query, setQuery] = useState<string>(valueProp?.name ?? "");
  const [results, setResults] = useState<Patient[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectedIdRef = useRef<number | null>(valueProp?.id ?? null);
  const ignoreNextFetchRef = useRef<boolean>(false);

  useEffect(() => {
    setQuery(valueProp?.name ?? "");
    setResults([]);
    setHighlightedIndex(-1);
    selectedIdRef.current = valueProp?.id ?? null;
    ignoreNextFetchRef.current = true;
  }, [valueProp]);

  useEffect(() => {
    if (ignoreNextFetchRef.current) {
      ignoreNextFetchRef.current = false;
      return;
    }

    if (query.length < 2) {
      setResults([]);
      setHighlightedIndex(-1);
      return;
    }

    setLoading(true);
    const q = query;
    fetch(`/patients/autocomplete?q=${encodeURIComponent(q)}`)
        .then((res) => res.json())
        .then((data: Patient[]) => {
          if (q !== query) return;
          setResults(data);
          setHighlightedIndex(data.length ? 0 : -1);
        })
        .catch((err) => {
          console.error("[PatientAutocomplete] autocomplete fetch error:", err);
          setResults([]);
        })
        .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setResults([]);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (p: Patient) => {
    console.log("[PatientAutocomplete] selected:", p);
    selectedIdRef.current = p.id;
    onChange?.(p);
    setQuery(p.name);
    setResults([]);
    setHighlightedIndex(-1);
    ignoreNextFetchRef.current = true;
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!results.length) return;
      setHighlightedIndex((prev) => (prev + 1) % results.length);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!results.length) return;
      setHighlightedIndex((prev) => (prev - 1 + results.length) % results.length);
      return;
    }
    if (e.key === "Escape") {
      setResults([]);
      setHighlightedIndex(-1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (results.length && highlightedIndex >= 0 && highlightedIndex < results.length) {
        handleSelect(results[highlightedIndex]);
        return;
      }

      if (valueProp && selectedIdRef.current === valueProp.id && query === valueProp.name) {
        onChange?.(valueProp);
        ignoreNextFetchRef.current = true;
      }
    }
  };

  return (
      <div ref={containerRef} className="relative w-full max-w-md">
        <Input
            ref={inputRef}
            placeholder={placeholder}
            value={query}
            onChange={(e) => {
              const value = e.target.value;
              if (selectedIdRef.current && valueProp && value !== valueProp.name) {
                selectedIdRef.current = null;
                onChange?.(null);
              }
              setQuery(value);
            }}
            onKeyDown={handleKeyDown}
            className="focus:ring-0 focus:border-gray-300"
        />

        {loading && (
            <div className="absolute right-2 top-2 text-xs text-muted-foreground">Loading…</div>
        )}

        {results.length > 0 && (
            <ul className="absolute z-10 w-full mt-1 max-h-[200px] overflow-y-auto border bg-white shadow rounded">
              {results.map((p, idx) => (
                  <li
                      key={p.id}
                      className={`p-2 cursor-pointer truncate ${idx === highlightedIndex ? "bg-gray-100" : ""}`}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelect(p);
                      }}
                  >
                    {p.name}
                  </li>
              ))}
            </ul>
        )}
      </div>
  );
}
